import { useEffect, useRef, useState } from 'react';

import Logo from '../../assets/images/logo.png';
import AppButton from '../base/button';
import CurrencyExchange from './new-payout/currency-exchange';
import { useAuthStore, useRateStore } from '../../store';
import Modal from '../base/modal';
import RadioButton from '../base/radio';
import BeneficiaryDetails from './new-payout/beneficiary-details';
import DocumentsUpload from './new-payout/documents-upload';
import ReviewPayout from './new-payout/review-payout';
import PayoutRequestSuccess from './new-payout/payout-request-success';
import { useBeneficiaryStore } from '../../store';
import {
  IBeneficiaryPayload,
  PaymentMethodEnum,
} from '../../models/beneficiary.model';
import BaseLabelText from '../base/label-text';
import usePayoutStore from '../../store/payout';
import toast from 'react-hot-toast';
import useDocumentStore from '../../store/document';
import CodeVerification from '../base/code-verification';
import { maskEmail } from '../../utils/string.util';
import GoBack from '../base/go-back';

type StepRefType = {
  saveData: () => void;
  getErrors: () => Record<string, any>;
};

const NewPayout = () => {
  const [step, setStep] = useState(1);
  const currentStepRef = useRef<StepRefType | null>(null);
  const [showNegotiateRateModal, setShowNegotiateRateModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // Load the auth state from local storage
  const { loadAuthStateFromStorage } = useAuthStore();
  const authState = loadAuthStateFromStorage();

  const {
    getAllBeneficiary,
    beneficiaries,
    beneficiaryPayload,
    saveBeneficiary,
    loading: isLoadingBeneficiary,
    setBeneficiaryPayload,
    resetBeneficiaryPayload,
  } = useBeneficiaryStore();
  const {
    payoutPayload,
    payoutSummary,
    beneficiaryFormState,
    setPayoutPayload,
    initiatePayout,
    loading: loadingPayout,
    resetPayoutPayload,
    getOtp,
    setBeneficiaryFormState,
  } = usePayoutStore();
  const {
    uploadDocument,
    removeDocument,
    loading: loadingDocument,
  } = useDocumentStore();

  const handleFileUpload = async (
    uploaded: boolean,
    file: File | null,
    base64: string | null,
  ) => {
    if (uploaded && file && base64) {
      const document = {
        base64File: base64,
        fileName: file.name,
        documentType: '',
      };
      const response = await uploadDocument(document);
      if (response && response.isSuccess) {
        toast.success('Document uploaded successfully.');
        setPayoutPayload({
          ...payoutPayload,
          proofOfPaymentDocumentId: response.value.fileId,
        });
      }
    }
  };

  const handleFileDelete = async () => {
    if (payoutPayload.proofOfPaymentDocumentId) {
      const response = await removeDocument(
        payoutPayload.proofOfPaymentDocumentId,
      );
      if (response && response.isSuccess) {
        toast.success('Document deleted successfully.');
        setPayoutPayload({
          ...payoutPayload,
          proofOfPaymentDocumentId: '',
        });
      }
    }
  };

  useEffect(() => {
    if (payoutPayload.toCurrencyCode) {
      getAllBeneficiary({
        page: 1,
        pageSize: 100,
        currencyCode: payoutPayload.toCurrencyCode,
      });
    }
  }, [payoutPayload.toCurrencyCode]);

  const handleNext = () => {
    if (currentStepRef.current) {
      currentStepRef.current.saveData();
    }

    setTimeout(() => {
      const currentErrors = currentStepRef.current?.getErrors?.() || {};
      const isValid = Object.keys(currentErrors).length === 0;

      if (isValid) {
        setStep((prev) => Math.min(prev + 1, 5));
      } else {
        console.error('Validation failed. Fix the errors before proceeding.');
      }
    }, 100);
  };

  const contactMethodOptions: Array<{ label: string; value: string }> = [
    { label: 'Phone Call', value: 'phone' },
    { label: 'WhatsApp', value: 'whatsapp' },
  ];
  const [selectedContactMethod, setSelectedContactMethod] = useState('email');
  const handleContactMethodChange = (value: string) => {
    setSelectedContactMethod(value);
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleCodeChange = (code: string) => {
    setPayoutPayload({
      ...payoutPayload,
      verificationCode: code,
    });
  };

  const handleSubmit = async () => {
    const response = await initiatePayout();
    if (response && response.isSuccess) {
      toast.success('Payout Initiated Successfully.');
      setShowVerifyModal(false);
      handleNext();
    }
  };

  const handleInitiateAnotherPayout = () => {
    resetPayoutPayload();
    resetBeneficiaryPayload();
    setBeneficiaryFormState('select_existing');
    setStep(1);
  };

  const showVerifyModalHandler = async () => {
    const response = await getOtp();
    if (response && response.isSuccess) {
      toast.success('OTP sent Successfully.');
      setShowVerifyModal(true);
    }
  };

  const handleCreateBeneficiary = async () => {
    try {
      setBeneficiaryPayload({
        ...beneficiaryPayload,
        currencyCode: payoutPayload.toCurrencyCode,
      });
      await getActiveCountries();
      const response = await saveBeneficiary();
      if (response && response.isSuccess) {
        toast.success('Beneficiary created successfully.');
        setPayoutPayload({
          ...payoutPayload,
          beneficiaryId: response.value.beneficiaryId,
        });
        handleNext();
      }
    } catch (err: any) {
      toast.error(`An error occured - ${err.message}`);
    }
  };

  const { getActiveCountries } = useRateStore();
  useEffect(() => {
    getActiveCountries();
  }, []);

  const handleNegotiateRate = () => {
    setShowNegotiateRateModal(true);
  };

  const validateBeneficiaryPayload = (
    payload: IBeneficiaryPayload,
  ): boolean => {
    if (
      !payload.bankName ||
      !payload.beneficiaryEmail ||
      !payload.accountOwnerType ||
      !payload.transactionType ||
      !payload.paymentMethod ||
      !payload.paymentType ||
      !payload.beneficiaryAddress ||
      (payload.accountOwnerType === 1 &&
        (!payload.firstName || !payload.lastName)) ||
      (payload.accountOwnerType !== 1 && !payload.businessName)
    ) {
      return false;
    }

    const address = payload.beneficiaryAddress;
    if (
      !address.fullAddress ||
      !address.streetLine1 ||
      !address.city ||
      !address.state ||
      !address.postalCode ||
      !address.countryCode
    ) {
      return false;
    }

    switch (payload.paymentType) {
      case PaymentMethodEnum.SWIFT:
        if (!payload.swiftPayment) return false;
        break;
      case PaymentMethodEnum.IBAN:
        if (!payload.ibanPayment) return false;
        break;
      case PaymentMethodEnum.USDLocal:
        if (!payload.localAccountPayment) return false;
        break;
      case PaymentMethodEnum.SWIFTUNKNOWN:
        if (!payload.unKnownPayment) return false;
        break;
      default:
        return false;
    }

    return true;
  };

  const disableForm = () => {
    if (step === 1) {
      return (
        !payoutPayload.amount ||
        !payoutPayload.fromCurrencyCode ||
        !payoutPayload.toAmount ||
        !payoutPayload.toCurrencyCode
      );
    } else if (step === 2) {
      return beneficiaryFormState === 'selected_existing'
        ? !payoutPayload.beneficiaryId
        : !validateBeneficiaryPayload(beneficiaryPayload);
    } else if (step === 3) {
      return !payoutPayload.proofOfPaymentDocumentId;
    } else if (step === 4) {
      return false;
    } else if (step === 5) {
      return false;
    }

    return true;
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="w-full bg-white fixed top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={Logo} alt="Logo" className="h-10" />
          </div>
        </div>
      </header>

      <div className="w-full fixed top-[4rem] z-10">
        <div className="w-full h-1 bg-[#F1F2F4] border border-[#F1F2F4] rounded-md">
          <div
            className="h-full bg-[#0040C2] rounded-md"
            style={{ width: `${(step / 5) * 100}%` }}
          />
        </div>
      </div>

      <main className="flex-grow my-[4rem] w-1/2 mx-auto py-12">
        <div className="flex flex-col gap-3 pb-24">
          <GoBack text="Back to dashboard" />
          {step !== 5 && (
            <h2 className="text-2xl font-bold text-[#111827]">
              {' '}
              {step === 4 ? 'Review Your Payout' : 'New Payout'}{' '}
            </h2>
          )}
          {step === 1 && (
            <CurrencyExchange onNegotiateRate={handleNegotiateRate} />
          )}
          {step === 2 && <BeneficiaryDetails beneficiaries={beneficiaries} />}
          {step === 3 && (
            <DocumentsUpload
              onFileUpload={handleFileUpload}
              onFileDelete={handleFileDelete}
            />
          )}
          {step === 4 && <ReviewPayout onNegotiateRate={handleNegotiateRate} />}
          {step === 5 && (
            <PayoutRequestSuccess onNegotiateRate={handleNegotiateRate} />
          )}
        </div>
        {showNegotiateRateModal && <div>Modal Content</div>}
      </main>

      <Modal
        isOpen={showNegotiateRateModal}
        toggleModal={() => setShowNegotiateRateModal(false)}
        width="w-[818px] h-fit"
      >
        <div className="flex flex-col gap-8">
          <p className="text-xl font-bold leading-7">Negotiate Exchange Rate</p>

          <div className="flex gap-6">
            <div className="flex flex-col gap-2">
              <p>Current exchange rate</p>
              <p>0.85 EUR/USD</p>
            </div>
            <div className="flex flex-col gap-2">
              <p>Current exchange rate</p>
              <p>0.85 EUR/USD</p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-base font-bold leading-7 -mb-2.5">
              Preferred Contact Method
            </p>
            <div className="flex flex-col gap-3.5">
              <RadioButton
                title=""
                name="transactionType"
                display={'flex-col gap-2'}
                options={contactMethodOptions}
                selectedValue={selectedContactMethod}
                onChange={handleContactMethodChange}
              />
            </div>
          </div>
        </div>

        {/* <AppButton
          label="Request Negotiation"
          // clicked={requestNegotiation}
          loading={loading}
        /> */}
      </Modal>

      {showVerifyModal && (
        <CodeVerification
          isOpen={showVerifyModal}
          toggleModal={() => setShowVerifyModal(false)}
          verifyInput={handleSubmit}
          loading={loadingPayout}
          email={maskEmail(authState?.email ?? '')}
          onCodeChange={handleCodeChange}
          resendCode={showVerifyModalHandler}
        />
      )}

      <footer className="w-full bg-white border-t border-gray-200 fixed bottom-0 z-10">
        <div className="py-4 flex items-center gap-16 justify-between w-1/2 mx-auto">
          <div className="bg-[#F8F8F8] border-dashed border border-[#CBD5E0] rounded-xl w-3/5 flex items-start gap-6 justify-between px-4 py-4">
            <BaseLabelText
              label="Exchange Rate"
              value={payoutSummary.exchangeRateText}
              type="vertical"
            />
            <BaseLabelText
              label="You Send"
              value={payoutSummary.sendingAmountText}
              type="vertical"
            />
            <BaseLabelText
              label="Recipient Gets"
              value={payoutSummary.receivingAmountText}
              type="vertical"
            />
          </div>

          <div className="flex gap-4 justify-end">
            {step > 1 && step < 5 && (
              <AppButton
                label="Back"
                type="light"
                clicked={handleBack}
                extraClass="!w-fit"
              />
            )}
            {step > 0 && (
              <AppButton
                fullWidth={false}
                label={
                  step === 4
                    ? 'Submit Payout Request'
                    : step === 5
                      ? 'Initiate Another Payout'
                      : 'Continue'
                }
                disabled={disableForm()}
                clicked={
                  step === 5
                    ? handleInitiateAnotherPayout
                    : step === 4
                      ? showVerifyModalHandler
                      : step === 2 && beneficiaryFormState === 'add_new'
                        ? handleCreateBeneficiary
                        : handleNext
                }
                loading={
                  isLoadingBeneficiary || loadingDocument || loadingPayout
                }
              />
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewPayout;
